/* eslint-disable complexity */
import { useDispatch } from 'react-redux';
import { Permission, Workspace, WorkspacesListingItem } from '@futrli-sdk/workspaces';
import {
  Logger,
  updateWorkspaces,
  useMobileOrTableWindowResize,
  getUpdatedWorkspacesAfterSubmittingSettings
} from '@futrli/shared-services';
import { UISystemIcon, toastError } from '@futrli/ui-components';
import React, { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { MobileNavBarScaffold } from '../../app/components/responsive/mobile-navbar-scaffold';
import { DesktopNavBarScaffold } from '../../app/components/responsive/desktop-navbar-scaffold';

import navbar from '../../app/components/navbar.scss';
import { SettingsContainer } from '../../settings/components/settings-container';
import { WorkspaceDropdown } from './workspace-dropdown';
import { updateWorkspace as updateWorkspaceApi } from '../api';
import { MobileMenuDrawer } from '../../app/components/responsive/mobile-menu-drawer';
import { HelpList } from '../../help/components/help-list';
import { WorkspaceSettingsSidebar } from './workspace-settings-sidebar';

interface Props {
  readonly workspaces: Workspace[] | WorkspacesListingItem[];
  readonly workspace: WorkspacesListingItem;
  readonly permissions: Permission[];
}

export const WorkspacePredictSettingsNavBar: React.FC<Props> = ({
  workspace,
  workspaces,
  permissions
}): JSX.Element => {
  const flags = useFlags();
  const [isLoading, setIsLoading] = useState(false);
  const [showRenameDialog, setShowRenameDialog] = useState(false);

  const dispatch = useDispatch();

  const { workspaceId: tempWorkspaceId } = useParams();
  const workspaceId = tempWorkspaceId || '';
  const isMobileOrTableWS = useMobileOrTableWindowResize();

  const handleEditClick = useCallback(() => setShowRenameDialog(true), []);

  const handleSubmit = useCallback(
    async (newWorkspaceName: string, isDefaultWorkspace: boolean) => {
      try {
        setIsLoading(true);

        await updateWorkspaceApi(workspace.id, {
          is_default_workspace: isDefaultWorkspace,
          ...(workspace.member.role.super_admin ? { name: newWorkspaceName || null } : {})
        });

        const updatedWorkspaces = getUpdatedWorkspacesAfterSubmittingSettings(
          workspaces as WorkspacesListingItem[],
          workspace,
          newWorkspaceName,
          isDefaultWorkspace
        );

        updateWorkspaces(updatedWorkspaces, dispatch);
        setShowRenameDialog(false);
      } catch (e) {
        Logger.error(e as string);
        toastError();
      } finally {
        setIsLoading(false);
      }
    },
    [workspace, workspaces, dispatch]
  );

  const handleToggle = useCallback(() => setShowRenameDialog(!showRenameDialog), [showRenameDialog]);

  const renderWorkspacesSelector = (): JSX.Element => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <WorkspaceDropdown selected={workspace} workspaces={workspaces} />
      {((workspace as WorkspacesListingItem).member.role.super_admin || flags.defaultWorkspace) && (
        <div
          className={navbar.editIconContainer}
          onClick={handleEditClick}
          style={workspaces.length === 1 ? { marginLeft: 10 } : {}}
        >
          <UISystemIcon iconName="edit-icon-light" />
        </div>
      )}
    </div>
  );

  if (!permissions) {
    return <React.Fragment />;
  }

  if (isMobileOrTableWS) {
    return (
      <>
        <MobileNavBarScaffold
          left={renderWorkspacesSelector()}
          right={
            <MobileMenuDrawer
              menuItems={[
                <SettingsContainer key="settingsContainerId" permissions={permissions} workspaceId={workspaceId} />,
                <HelpList key="helpListComponentId" />
              ]}
            />
          }
        />
        {showRenameDialog && (
          <WorkspaceSettingsSidebar
            isOpen={true}
            onSubmit={handleSubmit}
            onToggle={handleToggle}
            workspace={workspace}
            isLoading={isLoading}
            isSuperAdmin={(workspace as WorkspacesListingItem).member.role.super_admin}
            isDefaultWorkspace={(workspace as WorkspacesListingItem).member.is_default_workspace}
          />
        )}
      </>
    );
  }

  return (
    <>
      <DesktopNavBarScaffold customClassName={navbar.flexOneThird} left={renderWorkspacesSelector()} center={<></>} />
      {showRenameDialog && (
        <WorkspaceSettingsSidebar
          isOpen={true}
          onSubmit={handleSubmit}
          onToggle={handleToggle}
          workspace={workspace}
          isLoading={isLoading}
          isSuperAdmin={(workspace as WorkspacesListingItem).member.role.super_admin}
          isDefaultWorkspace={(workspace as WorkspacesListingItem).member.is_default_workspace}
        />
      )}
    </>
  );
};
